import { useEffect, useState } from 'react';
import { usePromoCodeLazyQuery } from 'src/generated/graphql';
import useDebounce from 'src/hooks/useDebounce';
import { Discount, Plan } from '../types';

interface useCheckPromoCodeArgs {
  plan: Plan;
  promoCode: string;
  currency?: string;
}

const initialDiscountObj = {
  percentOff: 0,
  amountOff: 0,
  terms: null,
  id: null,
  duration: null,
  trialDays: null,
};

function useCheckPromoCode({ promoCode, plan, currency }: useCheckPromoCodeArgs) {
  const [discount, setDiscount] = useState<Discount>(initialDiscountObj);
  // keep track of loading state separately to useLazyQuery, because we debounce input
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [getPromoCode, { error }] = usePromoCodeLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: ({ promoCode: receivedPromoCode }) => {
      setIsLoading(false);
      if (!receivedPromoCode) {
        setDiscount(initialDiscountObj);
      } else {
        const {
          percent_off: percentOff,
          amount_off: amountOff,
          terms,
          id,
          duration,
          trial_days: trialDays,
        } = receivedPromoCode;
        setDiscount({
          percentOff: percentOff || 0,
          amountOff: amountOff || 0,
          terms: terms ?? null,
          duration: duration ?? null,
          trialDays: trialDays ?? null,
          id,
        });
      }
    },
    onError: () => {
      setIsLoading(false);
      setDiscount(initialDiscountObj);
    },
  });

  useEffect(() => {
    if (promoCode) {
      setIsLoading(true);
    }
  }, [promoCode]);

  const debouncedPromoCode = useDebounce(promoCode, 1000);
  useEffect(() => {
    if (debouncedPromoCode && plan) {
      getPromoCode({ variables: { promoCode: debouncedPromoCode, planType: plan, currency } });
    }
  }, [getPromoCode, debouncedPromoCode, plan, currency]);

  const shouldResetDiscount = (error && discount.percentOff !== 0) || (discount.id && !promoCode);
  useEffect(() => {
    if (shouldResetDiscount) {
      setIsLoading(false);
      setDiscount(initialDiscountObj);
    }
  }, [shouldResetDiscount, setDiscount]);

  return { isLoading, discount };
}

export default useCheckPromoCode;
